import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowUpRight,
  BatteryMedium,
  CircleDot,
  Compass,
  Flag,
  Gauge,
  KeyRound,
  MapPin,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ALERT_ICON, SeverityBadge } from '@/components/alerts/severity-badge';
import { StatusBadge } from '@/components/common/status-badge';
import { VehicleThumb } from '@/components/vehicles/vehicle-thumb';
import { Button } from '@/components/ui/button';
import { PlaceLine } from '@/components/common/place-line';
import { refDoc, refId } from '@/lib/api/refs';
import { absoluteTime, coordinate, duration, fullName, km, percent, relativeTime, speed } from '@/lib/format';
import { useAlerts } from '@/lib/queries/alerts';
import { useUserDirectory } from '@/lib/queries/admin';
import { useTrips } from '@/lib/queries/trips';
import { useVehicleDirectory } from '@/lib/queries/vehicles';
import { cn } from '@/lib/utils';
import { contactSummary, type LiveVehicle } from '@/lib/vehicle-status';
import type { UserRef } from '@/types/api';

const TABS = [
  { value: 'overview', label: 'Vehicle information' },
  { value: 'trip', label: 'Trip details' },
  { value: 'alerts', label: 'Events & warnings' },
] as const;

type Tab = (typeof TABS)[number]['value'];

/**
 * Bottom sheet over the map for the selected vehicle. It has the room the
 * marker popover did not — a waypoint stepper for the current trip and a live
 * alert feed — and it never occludes the marker it describes.
 */
export function VehicleDetailPanel({
  entry,
  onClose,
}: {
  entry: LiveVehicle;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>('overview');
  const { vehicle } = entry;

  return (
    <motion.section
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 40, opacity: 0 }}
      transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
      className="pointer-events-auto overflow-hidden rounded-t-2xl bg-card shadow-float"
    >
      <header className="flex items-center gap-3 px-4 pt-3.5">
        <VehicleThumb make={vehicle.make} model={vehicle.model} status={entry.status} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-[15px] font-bold tracking-tight">
              {vehicle.make} {vehicle.model}
            </h2>
            <StatusBadge status={entry.status} />
          </div>
          <p className="tnum truncate text-[11.5px] text-muted-foreground">
            ID : {vehicle.plateNumber}
            {vehicle.group ? ` · ${vehicle.group}` : ''} · {contactSummary(entry)}
          </p>
        </div>

        <Button asChild size="sm" variant="secondary" className="h-9 gap-1.5 rounded-lg text-[12.5px]">
          <Link to={`/vehicles/${vehicle.id}`}>
            View details <ArrowUpRight className="size-3.5" />
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-9 shrink-0 text-muted-foreground"
          onClick={onClose}
          aria-label="Close panel"
        >
          <X className="size-4" />
        </Button>
      </header>

      <nav className="flex gap-1 border-b px-3 pt-2.5">
        {TABS.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setTab(item.value)}
            className={cn(
              'relative px-3 pb-2.5 text-[13px] font-semibold transition-colors',
              tab === item.value
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {item.label}
            {tab === item.value && (
              <motion.span
                layoutId="panel-tab"
                className="absolute inset-x-2 -bottom-px h-[2.5px] rounded-full bg-primary"
                transition={{ type: 'spring', stiffness: 480, damping: 38 }}
              />
            )}
          </button>
        ))}
      </nav>

      <div className="max-h-[210px] min-h-[132px] overflow-y-auto px-4 py-3.5">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.16 }}
          >
            {tab === 'overview' && <OverviewPane entry={entry} />}
            {tab === 'trip' && <TripPane entry={entry} />}
            {tab === 'alerts' && <AlertsPane entry={entry} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.section>
  );
}

function OverviewPane({ entry }: { entry: LiveVehicle }) {
  const { data: directory } = useUserDirectory();
  const { data: vehicles } = useVehicleDirectory();

  // The live projection carries no driver, so resolve through the full record.
  const driverRef =
    vehicles?.byId.get(entry.vehicle.id)?.assignedDriverId ?? entry.vehicle.assignedDriverId;
  const driverId = refId(driverRef);
  const driver =
    refDoc<UserRef>(driverRef) ?? (driverId ? directory?.byId.get(driverId) : undefined);

  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3.5 sm:grid-cols-3 lg:grid-cols-6">
      <Field icon={Gauge} label="Speed" value={speed(entry.speedKph)} />
      <Field icon={Compass} label="Heading" value={`${Math.round(entry.headingDeg)}°`} />
      <Field icon={KeyRound} label="Ignition" value={entry.ignition ? 'On' : 'Off'} />
      <Field
        icon={BatteryMedium}
        label="Device battery"
        value={percent(entry.vehicle.lastKnown?.batteryPercent)}
      />
      {/* /vehicles/live returns a trimmed projection: the top-level odometer is
          absent, but the one carried on the last fix is not. */}
      <Field icon={CircleDot} label="Odometer" value={km(entry.vehicle.lastKnown?.odometerKm, 0)} />
      <Field icon={MapPin} label="Driver" value={driver ? fullName(driver) : 'Unassigned'} />
      <div className="col-span-2 sm:col-span-3 lg:col-span-6">
        <p className="label-micro">Last known position</p>
        <PlaceLine lat={entry.lat} lng={entry.lng} />
      </div>
    </dl>
  );
}

/** The reference's horizontal waypoint stepper, fed by the current trip. */
function TripPane({ entry }: { entry: LiveVehicle }) {
  const { data } = useTrips({
    vehicleId: entry.vehicle.id,
    limit: 1,
    sortBy: 'startedAt',
    sortOrder: 'desc',
  });

  const trip = data?.items?.[0];

  const steps = useMemo(() => {
    if (!trip) return [];
    return [
      {
        label: 'Departure point',
        // Stored when the trip closed; the coordinate is the fallback for
        // trips that ended while the geocoder was unreachable.
        place:
          trip.startPlace ??
          coordinate(trip.startPosition.coordinates[1], trip.startPosition.coordinates[0]),
        time: absoluteTime(trip.startedAt, 'd MMM, h:mm a'),
        done: true,
      },
      {
        label: trip.status === 'IN_PROGRESS' ? 'Current position' : 'Final destination',
        place:
          trip.status === 'IN_PROGRESS'
            ? coordinate(entry.lat, entry.lng)
            : (trip.endPlace ?? coordinate(entry.lat, entry.lng)),
        time:
          trip.status === 'IN_PROGRESS'
            ? relativeTime(entry.updatedAt)
            : absoluteTime(trip.endedAt, 'd MMM, h:mm a'),
        done: trip.status !== 'IN_PROGRESS',
      },
    ];
  }, [trip, entry]);

  if (!trip) {
    return (
      <p className="py-6 text-center text-[13px] text-muted-foreground">
        No trip recorded for this vehicle yet. Trips are built from telemetry in the background.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-x-8 gap-y-2">
        <Field icon={CircleDot} label="Distance" value={km(trip.distanceKm)} />
        <Field icon={Gauge} label="Average" value={speed(trip.avgSpeedKph)} />
        <Field icon={Gauge} label="Peak" value={speed(trip.maxSpeedKph)} />
        <Field icon={Flag} label="Duration" value={duration(trip.durationSeconds)} />
      </div>

      <ol className="flex items-start gap-0">
        {steps.map((step, index) => (
          <li key={step.label} className="relative flex min-w-0 flex-1 flex-col items-start gap-1.5">
            <div className="flex w-full items-center">
              <span
                className={cn(
                  'z-10 flex size-7 shrink-0 items-center justify-center rounded-full border-2 bg-card',
                  step.done ? 'border-primary text-primary' : 'border-moving text-moving',
                )}
              >
                <MapPin className="size-3.5" strokeWidth={2.4} />
              </span>
              {index < steps.length - 1 && (
                <span className="-ml-1 h-0.5 flex-1 rounded-full bg-border" />
              )}
            </div>
            <div className="min-w-0 pr-4">
              <p className="truncate text-[12.5px] font-semibold">{step.label}</p>
              <p className="tnum truncate text-[11.5px] text-muted-foreground">{step.place}</p>
              <p className="tnum truncate text-[11px] text-muted-foreground">{step.time}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function AlertsPane({ entry }: { entry: LiveVehicle }) {
  const { data } = useAlerts({
    vehicleId: entry.vehicle.id,
    limit: 6,
    sortBy: 'occurredAt',
    sortOrder: 'desc',
  });

  const alerts = data?.items ?? [];

  if (alerts.length === 0) {
    return (
      <p className="py-6 text-center text-[13px] text-muted-foreground">
        Nothing has been raised against this vehicle.
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {alerts.map((alert) => {
        const Icon = ALERT_ICON[alert.type] ?? MapPin;
        return (
          <li key={alert.id} className="flex items-start gap-2.5 rounded-lg bg-muted/50 px-3 py-2">
            <span
              className={cn(
                'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg',
                alert.severity === 'CRITICAL'
                  ? 'bg-alert-soft text-alert'
                  : alert.severity === 'WARNING'
                    ? 'bg-idle-soft text-idle'
                    : 'bg-info-soft text-info',
              )}
            >
              <Icon className="size-3.5" strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate text-[12.5px] font-semibold">{alert.title}</p>
                <SeverityBadge severity={alert.severity} />
              </div>
              <p className="truncate text-[11.5px] text-muted-foreground">{alert.message}</p>
            </div>
            <span className="tnum shrink-0 text-[11px] text-muted-foreground">
              {relativeTime(alert.occurredAt)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Field({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Gauge;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="label-micro flex items-center gap-1.5">
        <Icon className="size-3.5" />
        {label}
      </p>
      <p className="tnum mt-0.5 truncate text-[13.5px] font-semibold">{value}</p>
    </div>
  );
}

